// lib/firebase.ts
import { getApps, initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signInWithCredential,
  PhoneAuthProvider,
  type User as FirebaseAuthUser,
  type ConfirmationResult,
  type Auth,
} from "firebase/auth";

import { fetchFirebaseConfig } from "@/api/config";
import api from "@/api/axios";

/**
 * Firebase phone auth on web uses reCAPTCHA v2 (invisible or widget).
 * Invisible mode should be bound to the ID of the button that submits the form. :contentReference[oaicite:2]{index=2}
 */

let appPromise: Promise<FirebaseApp> | null = null;
let authPromise: Promise<Auth> | null = null;

// Keep verifiers by "button id"
const recaptchaVerifiers = new Map<string, RecaptchaVerifier>();
const recaptchaWidgetIds = new Map<string, number>();

// Session storage keys to survive refresh on OTP page
export const SESSION_PHONE_KEY = "auth_phone_number";
export const SESSION_VERIFICATION_ID_KEY = "auth_verification_id";

function isBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

async function initApp(): Promise<FirebaseApp> {
  if (getApps().length) return getApps()[0]!;
  if (!appPromise) {
    appPromise = (async () => {
      const cfg = await fetchFirebaseConfig();
      return initializeApp(cfg);
    })();
  }
  return appPromise;
}

export async function getAuthSingleton(): Promise<Auth> {
  if (!authPromise) {
    authPromise = (async () => {
      const app = await initApp();
      return getAuth(app);
    })();
  }
  return authPromise;
}

export async function setupAuthStateListener(
  onAuthStateChange: (user: FirebaseAuthUser | null) => void
): Promise<() => void> {
  const auth = await getAuthSingleton();
  const unsubscribe = onAuthStateChanged(auth, user => onAuthStateChange(user));
  return unsubscribe;
}

export async function signOutUser() {
  const auth = await getAuthSingleton();
  await auth.signOut();
  delete api.defaults.headers.common.Authorization;
}

export async function getOrCreateInvisibleRecaptchaVerifier(
  buttonId: string,
  options?: {
    onSolved?: () => void;
    onExpired?: () => void;
    languageCode?: string; // optional localization
    useDeviceLanguage?: boolean;
  }
): Promise<RecaptchaVerifier> {
  if (!isBrowser()) {
    throw new Error("reCAPTCHA can only be initialized in the browser.");
  }

  const existingButton = document.getElementById(buttonId);
  if (!existingButton) {
    throw new Error(
      `Missing button element with id="${buttonId}" for RecaptchaVerifier.`
    );
  }

  const cached = recaptchaVerifiers.get(buttonId);
  if (cached) return cached;

  const auth = await getAuthSingleton();

  if (options?.useDeviceLanguage) {
    auth.useDeviceLanguage();
  } else if (options?.languageCode) {
    auth.languageCode = options.languageCode;
  }

  const verifier = new RecaptchaVerifier(auth, buttonId, {
    size: "invisible",
    callback: () => options?.onSolved?.(),
    "expired-callback": () => options?.onExpired?.(),
  });

  const widgetId = await verifier.render();
  recaptchaVerifiers.set(buttonId, verifier);
  recaptchaWidgetIds.set(buttonId, widgetId);

  return verifier;
}


export function resetRecaptcha(buttonId: string) {
  if (!isBrowser()) return;
  const widgetId = recaptchaWidgetIds.get(buttonId);
  // grecaptcha is injected by the recaptcha script
  const grecaptchaAny = (window as any).grecaptcha;
  if (widgetId != null && grecaptchaAny?.reset) {
    grecaptchaAny.reset(widgetId);
  }
}

export function clearRecaptchaVerifier(buttonId: string) {
  const v = recaptchaVerifiers.get(buttonId);
  if (v) {
    try {
      v.clear();
    } catch {
      // ignore
    }
    recaptchaVerifiers.delete(buttonId);
  }
  recaptchaWidgetIds.delete(buttonId);
}

export function clearAllRecaptchaVerifiers() {
  for (const id of recaptchaVerifiers.keys()) {
    clearRecaptchaVerifier(id);
  }
}

export async function sendOtpSms(params: {
  phoneNumberE164: string;
  recaptchaButtonId: string;
  onSolved?: () => void;
  onExpired?: () => void;
  languageCode?: string;
  useDeviceLanguage?: boolean;
}): Promise<ConfirmationResult> {
  const auth = await getAuthSingleton();
  const verifier = await getOrCreateInvisibleRecaptchaVerifier(
    params.recaptchaButtonId,
    {
      onSolved: params.onSolved,
      onExpired: params.onExpired,
      languageCode: params.languageCode,
      useDeviceLanguage: params.useDeviceLanguage,
    }
  );

  try {
    return await signInWithPhoneNumber(auth, params.phoneNumberE164, verifier);
  } catch (err) {
    resetRecaptcha(params.recaptchaButtonId);
    throw err;
  }
}


export async function verifyOtpWithVerificationId(
  verificationId: string,
  code: string
) {
  const auth = await getAuthSingleton();
  const credential = PhoneAuthProvider.credential(verificationId, code);
  const result = await signInWithCredential(auth, credential);
  const token = await result.user.getIdToken(false);
  return { user: result.user, token };
}

// Helpers to store/retrieve session state for OTP page
export function storePhoneSession(
  phoneNumberE164: string,
  verificationId: string
) {
  if (!isBrowser()) return;
  sessionStorage.setItem(SESSION_PHONE_KEY, phoneNumberE164);
  sessionStorage.setItem(SESSION_VERIFICATION_ID_KEY, verificationId);
}

export function readPhoneSession(): {
  phoneNumber: string | null;
  verificationId: string | null;
} {
  if (!isBrowser()) return { phoneNumber: null, verificationId: null };
  return {
    phoneNumber: sessionStorage.getItem(SESSION_PHONE_KEY),
    verificationId: sessionStorage.getItem(SESSION_VERIFICATION_ID_KEY),
  };
}

export function clearPhoneSession() {
  if (!isBrowser()) return;
  sessionStorage.removeItem(SESSION_PHONE_KEY);
  sessionStorage.removeItem(SESSION_VERIFICATION_ID_KEY);
}
